import { memo, useState } from "react";
import { ChevronDown, ChevronRight, ShieldAlert, CheckCircle2, EyeOff, KeyRound } from "lucide-react";

export type BreachRecord = {
  name: string;
  title: string;
  date?: string;
  pwnCount?: number;
  dataClasses?: string[];
  verified?: boolean;
  sensitive?: boolean;
  fabricated?: boolean;
  description?: string;
};

/** Extract breach records from a scan run's raw.collectors payload. */
export function extractBreachesFromRun(run: any): BreachRecord[] {
  const collectors = run?.raw?.collectors ?? run?.collectors ?? [];
  const hibp = Array.isArray(collectors)
    ? collectors.find((c: any) => c?.source === "HaveIBeenPwned breaches" && c?.status === "ok")
    : null;
  const list = (hibp?.data?.breaches ?? []) as any[];
  return list.map((b) => ({
    name: String(b.name ?? b.Name ?? ""),
    title: String(b.title ?? b.Title ?? b.name ?? "Unknown"),
    date: b.date ?? b.BreachDate,
    pwnCount: b.pwnCount ?? b.PwnCount,
    dataClasses: b.dataClasses ?? b.DataClasses ?? [],
    verified: !!(b.verified ?? b.IsVerified),
    sensitive: !!(b.sensitive ?? b.IsSensitive),
    fabricated: !!(b.fabricated ?? b.IsFabricated),
    description: b.description ?? b.Description,
  }));
}

export type HibpStatus = {
  state: "clean" | "hits" | "not-run" | "error";
  summary: string;
  email?: string;
  pasteCount: number;
  latestPasteDate?: string | null;
  pastes: Array<{ id: string; source: string; title: string | null; date: string | null; url: string | null; emailCount?: number }>;
};

/** Read the HIBP breach + paste collectors so the UI can always state a result. */
export function extractHibpStatus(run: any): HibpStatus {
  const collectors: any[] = run?.raw?.collectors ?? run?.collectors ?? [];
  const list = Array.isArray(collectors) ? collectors : [];
  const breach = list.find((c) => c?.source === "HaveIBeenPwned breaches");
  const paste = list.find((c) => c?.source === "HIBP pastes");
  const pastes = (paste?.data?.pastes ?? []) as any[];
  const count = Number(breach?.data?.count ?? 0);
  const state: HibpStatus["state"] =
    count > 0 ? "hits"
    : breach?.status === "error" ? "error"
    : breach?.data?.checked || breach?.status === "ok" ? "clean"
    : "not-run";
  return {
    state,
    summary: String(breach?.summary ?? "Credential exposure not checked for this target."),
    email: breach?.data?.email,
    pasteCount: Number(paste?.data?.count ?? 0),
    latestPasteDate: paste?.data?.latestPasteDate ?? null,
    pastes: pastes.map((p) => ({
      id: String(p?.id ?? ""), source: String(p?.source ?? "unknown"),
      title: p?.title ?? null, date: p?.date ?? null, url: p?.url ?? null, emailCount: p?.emailCount,
    })),
  };
}

function BreachRecordsListImpl({ breaches, status }: { breaches: BreachRecord[]; status?: HibpStatus }) {
  return (
    <section id="breach-records" className="glass rounded-xl p-4">
      <div className="flex items-center gap-2">
        <ShieldAlert className="h-3.5 w-3.5 text-danger" />
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
          Credential Exposure · HIBP · {breaches.length} record{breaches.length === 1 ? "" : "s"}
        </div>
      </div>
      {status && (
        <div
          className={`mt-3 rounded border px-2.5 py-2 text-xs ${
            status.state === "hits"
              ? "border-danger/40 bg-danger/5 text-danger"
              : status.state === "clean"
              ? "border-success/40 bg-success/5 text-success"
              : "border-panel-border bg-background/40 text-muted-foreground"
          }`}
        >
          <div className="font-mono text-[10px] uppercase tracking-widest">
            {status.state === "hits" ? "Exposed" : status.state === "clean" ? "Checked · clean" : status.state === "error" ? "Check incomplete" : "Not checked"}
          </div>
          <div className="mt-0.5">{status.summary}</div>
          {status.state === "not-run" && (
            <div className="mt-1 opacity-80">Add an email in narrow search to run the breach check.</div>
          )}
        </div>
      )}
      {breaches.length === 0 ? (
        !status ? (
          <div className="mt-3 text-xs text-muted-foreground">
            No credential exposures detected via HaveIBeenPwned.
          </div>
        ) : null
      ) : (
        <ul className="mt-3 space-y-2">
          {breaches.map((b) => (
            <BreachRow key={b.name || b.title} b={b} />
          ))}
        </ul>
      )}
      {status && status.pasteCount > 0 && (
        <div className="mt-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber">
            Public pastes · {status.pasteCount}
          </div>
          <ul className="mt-1.5 space-y-1">
            {status.pastes.slice(0, 8).map((p) => (
              <li key={p.id || `${p.source}-${p.date}`} className="text-xs text-muted-foreground">
                <span className="font-mono text-[10px] uppercase">{p.source}</span>
                {p.date && <span className="ml-2 font-mono text-[10px]">{String(p.date).slice(0, 10)}</span>}
                {p.title && <span className="ml-2">{p.title}</span>}
                {typeof p.emailCount === "number" && <span className="ml-2">· {p.emailCount.toLocaleString()} emails</span>}
                {p.url && (
                  <a href={p.url} target="_blank" rel="noopener noreferrer" className="ml-2 text-cyan underline underline-offset-2">
                    view
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function BreachRow({ b }: { b: BreachRecord }) {
  const [open, setOpen] = useState(false);
  const hasPassword = (b.dataClasses ?? []).some((c) => /password/i.test(c));
  const sev = hasPassword ? "critical" : b.verified ? "high" : "medium";
  const sevCls =
    sev === "critical"
      ? "text-danger border-danger/50 bg-danger/10"
      : sev === "high"
      ? "text-[oklch(0.82_0.20_50)] border-[oklch(0.72_0.20_50)]/50 bg-[oklch(0.72_0.20_50)]/10"
      : "text-amber border-amber/50 bg-amber/10";

  return (
    <li className="rounded border border-panel-border bg-background/40">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-2 p-2 text-left"
      >
        {open ? <ChevronDown className="h-3 w-3 mt-1 flex-shrink-0" /> : <ChevronRight className="h-3 w-3 mt-1 flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center rounded-sm border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${sevCls}`}>
              {sev}
            </span>
            <span className="font-medium truncate">{b.title}</span>
            {b.date && <span className="font-mono text-[10px] text-muted-foreground">{b.date}</span>}
            {b.verified && (
              <span title="Verified breach" className="text-success">
                <CheckCircle2 className="h-3 w-3" />
              </span>
            )}
            {b.sensitive && (
              <span title="Sensitive breach" className="text-amber">
                <EyeOff className="h-3 w-3" />
              </span>
            )}
            {hasPassword && (
              <span title="Passwords exposed" className="text-danger">
                <KeyRound className="h-3 w-3" />
              </span>
            )}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {typeof b.pwnCount === "number" && b.pwnCount > 0 && (
              <span>{b.pwnCount.toLocaleString()} accounts · </span>
            )}
            {(b.dataClasses ?? []).slice(0, 6).join(" · ")}
          </div>
        </div>
      </button>
      {open && (
        <div className="border-t border-panel-border px-2 pb-2 pt-1.5">
          {(b.dataClasses ?? []).length > 0 && (
            <div className="mb-2 flex flex-wrap gap-1">
              {(b.dataClasses ?? []).map((dc) => (
                <span
                  key={dc}
                  className={`inline-flex rounded border px-1.5 py-0.5 font-mono text-[10px] ${
                    /password/i.test(dc)
                      ? "border-danger/40 bg-danger/10 text-danger"
                      : "border-panel-border bg-background/60 text-muted-foreground"
                  }`}
                >
                  {dc}
                </span>
              ))}
            </div>
          )}
          {b.description && (
            <div
              className="prose prose-invert prose-xs max-w-none text-xs text-muted-foreground [&_a]:text-cyan"
              dangerouslySetInnerHTML={{ __html: sanitizeBreachHtml(b.description) }}
            />
          )}
          {b.fabricated && (
            <div className="mt-1 text-[10px] text-muted-foreground italic">
              Note: HIBP flagged this breach as fabricated / unverified origin.
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function sanitizeBreachHtml(html: string): string {
  // HIBP descriptions are trusted from the API but restrict to a tiny allow-list.
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/on\w+="[^"]*"/gi, "")
    .replace(/on\w+='[^']*'/gi, "");
}

/** Memoised: these panels render large evidence sets and must not re-render on every keystroke. */
export const BreachRecordsList = memo(BreachRecordsListImpl);
